import React from 'react';
import debounce from 'debounce';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import PageBaseProps from 'types/PageBase';
import { LoginData } from 'modules/auth/types/InitialState';
import { ErrorTypeEnum, isApiErrorResponse } from 'types/Error';
import { ConnectionTypeLogin } from 'modules/dealers/types/ContextResponse';
import { PhoneNumber, SelfServiceType } from 'modules/selfServices/types/SelfService';

import { b } from 'utils/i18nUtils';
import { useDispatch, useSelector } from 'hooks';
import { validatePlateNumber } from 'utils/formUtils';

import { login } from 'modules/auth/thunk';
import { clearCodeLoginError, setLoginData, setNotFound } from 'modules/auth/actions';
import { closeRestrictedAccessModal, continueRestrictedAccessModal } from 'modules/restrictedAccess/actions';

import {
  getLoginData,
  isLoadingLogin,
  hasInvalidCodeError,
  isCodeFieldDisabled,
  getCodeRetryExpiration,
  getNotFoundSelfService,
  hasValidQrCode as hasValidQrCodeSelector,
  hasLocationIdError as hasLocationIdErrorSelector,
  hasRegistrationError as hasRegistrationErrorSelector,
} from 'modules/auth/selectors';
import {
  hasRestrictedAccess as hasRestrictedAccessSelector,
  isRestrictedModalOpen as isRestrictedModalOpenSelector,
} from 'modules/restrictedAccess/selectors';
import {
  getLegalNotice,
  isFlowTypeModalVisible,
  isSelfServiceLoading as isSelfServiceLoadingSelector,
} from 'modules/selfServices/selectors';
import {
  getConfidentialityPolicyLink,
  getConnectionType,
  getKioskName,
  getWorkflowsForAllDealers,
  isBreakdownAvailable,
  isCodeLoginEnabled,
} from 'modules/dealers/selectors';

import { FlowTypeModal, Icon, Input, PageHeader, Switch } from 'components/ui';
import Footer from 'components/ui/PageFooter';
import PhoneNumberInput from 'components/ui/PhoneNumberInput';
import RestrictedAccessModal from 'components/RestrictedAccessModal';

import useCountdown from 'hooks/useCountdown';

import LoginTerms from './LoginTerms';
import BreakDownInformation from './BreakDownInformation';
import BreakdownRedirectionModal from './BreakdownRedirectionModal';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';
import { QR_CODE_LENGTH } from 'modules/auth/constants';

const Login: React.FC<PageBaseProps> = ({ onNext, onPrev }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search, state: pageState } = useLocation();

  const loginData = useSelector(getLoginData);
  const kioskName = useSelector(getKioskName);
  const isLoading = useSelector(isLoadingLogin);
  const legalNotice = useSelector(getLegalNotice);
  const connectionTypes = useSelector(getConnectionType);
  const workflows = useSelector(getWorkflowsForAllDealers);
  const breakdownAvailable = useSelector(isBreakdownAvailable);
  const isFlowTypeModalOpen = useSelector(isFlowTypeModalVisible);
  const isNotFoundSelfService = useSelector(getNotFoundSelfService);
  const hasLocationIdError = useSelector(hasLocationIdErrorSelector);
  const hasRestrictedAccess = useSelector(hasRestrictedAccessSelector);
  const hasRegistrationError = useSelector(hasRegistrationErrorSelector);
  const isSelfServiceLoading = useSelector(isSelfServiceLoadingSelector);
  const isRestrictedModalOpen = useSelector(isRestrictedModalOpenSelector);
  const confidentialityPolicyLink = useSelector(getConfidentialityPolicyLink);

  const { registration = '', qrCode = '', phoneNumber, dealerId, brandId, isDriver } = loginData;

  const qrCodeAvailable = useSelector(isCodeLoginEnabled);
  const hasQrCodeError = useSelector(hasInvalidCodeError);
  const hasProperQrCode = useSelector(hasValidQrCodeSelector);
  const retryExpiration = useSelector(getCodeRetryExpiration);

  const { countdown } = useCountdown({ date: retryExpiration });
  const codeInputDisabled = useSelector(isCodeFieldDisabled) && countdown > 0;
  const sendQrCode = hasProperQrCode && !codeInputDisabled;

  const [locationId, setLocationId] = React.useState<number>();
  const [hasPhoneError, setHasPhoneError] = React.useState(false);
  const [plateNumberError, setPlateNumberError] = React.useState<string>();
  const [codeError, setCodeError] = React.useState<string>();

  const isAtDealership = pageState?.atDealership;
  const showKioskName = isAtDealership && Boolean(kioskName);
  const isCheckin = pageState?.type === SelfServiceType.CHECK_IN;
  const isBreakdown = pageState?.type === SelfServiceType.BREAKDOWN;
  const showRedirectionModal = isCheckin && breakdownAvailable && isNotFoundSelfService;
  const disabled = registration.length < 1
    || Boolean(plateNumberError)
    || (isBreakdown && (hasPhoneError || !phoneNumber?.local))
    || (isAtDealership && !locationId);

  const isNextDisabled = disabled && (!hasProperQrCode || codeInputDisabled);
  const licensePlateError =(qrCode.length === 0 || codeInputDisabled) && (plateNumberError || hasRegistrationError);

  const hasTerms = Boolean(legalNotice || confidentialityPolicyLink);
  // In MFA connection type, Terms will be display in the LoginVerify step.
  const displayTerms = hasTerms && !connectionTypes?.includes(ConnectionTypeLogin.MFA);

  const loginLabel = React.useMemo(() => {
    let label = (
      <FormattedMessage
        id="login.askLicensePlate"
        defaultMessage="Please enter the license plate of your vehicle in order to access your appointment"
      />
    );

    if (isBreakdown) {
      label = (
        <FormattedMessage
          id="login.askPlateBreakdown"
          defaultMessage="Please enter your phone number and the license plate of the vehicle."
        />
      );
    }

    if (isAtDealership) {
      label = (
        <FormattedMessage
          id="login.askLicensePlateAndId"
          defaultMessage="Please enter your license plate and the kiosk's location ID in order to access your appointment."
        />
      );
    }

    return label;
  }, [isBreakdown, isAtDealership]);

  const handleChangeLoginData = React.useCallback(
    (data: Partial<LoginData>) => {
      dispatch(setLoginData({ ...loginData, ...data }));
    },
    [dispatch, loginData],
  );

  const handleInputCodeChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (hasQrCodeError) { dispatch(clearCodeLoginError()); }
    handleChangeLoginData({ qrCode: value });
    if (value.length > 0 && value.length < QR_CODE_LENGTH) {
      setCodeError(intl.formatMessage(
        { id: 'login.code.tooShort', defaultMessage: 'The code must be {length} characters.' },
        { length: QR_CODE_LENGTH },
      ));
    } else {
      setCodeError(undefined);
    }
  }, [dispatch, handleChangeLoginData, hasQrCodeError, intl]);

  const handleInputPlateChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      handleChangeLoginData({ registration: value.toUpperCase() });
      setPlateNumberError(validatePlateNumber(value, isBreakdown, intl));
    },
    [intl, handleChangeLoginData, isBreakdown],
  );

  const handleLocationIdChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setLocationId(parseInt(value, 10));
  }, []);

  const handlePhoneNumberChange = React.useCallback(
    (newPhoneNumber: PhoneNumber) => handleChangeLoginData({ phoneNumber: newPhoneNumber }),
    [handleChangeLoginData],
  );

  const setIsDriver = React.useCallback(
    (value: boolean) => handleChangeLoginData({ isDriver: value }),
    [handleChangeLoginData],
  );

  const handleOnPrev = React.useCallback(() => {
    const hasBreakdownInstructions = isBreakdown && workflows[SelfServiceType.BREAKDOWN]?.includes(SelfServiceSteps.INSTRUCTIONS);
    if (hasBreakdownInstructions) {
      onPrev();
    } else {
      navigate(`/${search}`);
    }
  }, [isBreakdown, navigate, onPrev, search, workflows]);

  const handleNextClick = async () => {
    try {
      await dispatch(
        login({
          qrCode: sendQrCode ? qrCode : undefined,
          codeInputDisabled,
          registration,
          connectionTypes,
          type: pageState?.type,
          locationId: isAtDealership ? locationId : undefined,
          ...(isBreakdown
            ? {
              brandId,
              dealerId,
              phoneNumber,
              isDriver,
            }
            : {}),
        }),
      ).unwrap();

      if (hasProperQrCode || connectionTypes?.includes(ConnectionTypeLogin.MFA)) {
        onNext();
      }
    } catch (error) {
      if (isApiErrorResponse(error)) {
        // If error is MFA_CODE_TIMEOUT (cannot resend until timeout), we go to next page anyway: BMWVID-15237
        if (error.data.errorType === ErrorTypeEnum.MFA_CODE_TIMEOUT) {
          onNext();
        } else if (error.data.errorType === ErrorTypeEnum.SO_WRONG_KIOSK) {
          navigate(`/wrongKiosk${search}`);
        }
      }
    }
  };

  const handleKeyDown = debounce((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isNextDisabled && !isLoading) {
      handleNextClick();
    }
  }, 200);

  const handleHideRedirectionModal = React.useCallback(() => dispatch(setNotFound(false)), [dispatch]);
  const handleHideRestrictedAccessModal = React.useCallback(
    (shouldOpenGate: boolean) => dispatch(shouldOpenGate ? continueRestrictedAccessModal() : closeRestrictedAccessModal()),
    [dispatch],
  );

  const getCodeError = () =>{
    if (codeInputDisabled) {
      return intl.formatMessage(
        { id: 'login.code.maxAttemptsReached',
          defaultMessage: 'You have reached the maximum number of attempts. Please try again in {countdown} seconds.'
        },
        { countdown },
      );
    }

    if (hasQrCodeError) {
      return intl.formatMessage({ id: 'login.code.invalid', defaultMessage: 'Wrong code, please try again.' });
    }

    return codeError;
  };

  return (
    <>
      <PageHeader
        hideSteps
        onPrev={handleOnPrev}
        title={<FormattedMessage id="login.title" defaultMessage="Login" />}
      />
      <div className="main-content">
        <main className="content !m-auto">
          {showKioskName && (
            <div className="w-full p-4 bg-card-bg rounded-card flex items-center justify-center gap-x-4 border border-brand-high">
              <div>
                <div className="flex items-center justify-center size-10 bg-brand-high rounded-full">
                  <Icon className="fb-icon--warning text-2xl mb-1 kiosk:text-2xl text-white" />
                </div>
              </div>
              <p className="text-s">
                <FormattedMessage
                  values={{ b, locationId: kioskName }}
                  id="login.existingLocationId"
                  defaultMessage="Your keys are in the kiosk {kioskName}. Please present yourself there before proceeding."
                />
              </p>
            </div>
          )}
          <p className="text-center">{loginLabel}</p>
          {isBreakdown && (
            <>
              <PhoneNumberInput
                required
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                className="mt-10 pb-5 kiosk:mt-20 kiosk:pb-10 kioskSharebox:mt-10 kioskSharebox:pb-5 !mb-0"
                onError={setHasPhoneError}
              />
              <Switch
                size="small"
                checked={isDriver}
                onChange={setIsDriver}
                data-testid="switch-isBreakdown"
                className="border-b-2 pb-10 kioskSharebox:pb-10 kiosk:pb-20"
              >
                <FormattedMessage id="login.askBreakdownService" defaultMessage="I am the driver" />
              </Switch>
              <h3 className="mt-7 kiosk:mt-14 kioskSharebox:mt-7 text-center">
                <FormattedMessage id="login.vehicleInformation" defaultMessage="Vehicle information" />
              </h3>
            </>
          )}
          <Input
            maxLength={50}
            className="mt-4"
            value={registration}
            center={!isBreakdown}
            onKeyDown={handleKeyDown}
            labelClassName={!isBreakdown && 'text-center'}
            onChange={handleInputPlateChange}
            label={intl.formatMessage({
              id: 'login.licensePlate',
              defaultMessage: 'License plate',
            })}
            autoCorrect="off"
            spellCheck="false"
            error={licensePlateError}
            data-testid="plateNumber-input"
            data-layout="alternate"
          />
          {qrCodeAvailable && (
            <>
              <p className="text-center mt-4">
                <FormattedMessage
                  id="login.code.separatorPhrase"
                  defaultMessage="or enter the <b>code</b> received by email."
                  values={{ b }}
                />
              </p>
              <Input
                center
                type="password"
                value={qrCode}
                onKeyDown={handleKeyDown}
                maxLength={QR_CODE_LENGTH}
                className="mt-4 text-center"
                disabled={codeInputDisabled}
                onChange={handleInputCodeChange}
                label={intl.formatMessage({
                  id: 'login.code.label',
                  defaultMessage: 'Code',
                })}
                error={getCodeError()}
              />
            </>
          )}

          {isAtDealership && (
            <div>
              <Input
                center
                value={locationId}
                onKeyDown={handleKeyDown}
                error={hasLocationIdError}
                labelClassName="text-center"
                onChange={handleLocationIdChange}
                label={intl.formatMessage({
                  id: 'login.locationId',
                  defaultMessage: 'Location ID',
                })}
                type="number"
                autoCorrect="off"
                spellCheck="false"
                inputMode="numeric"
                role="textbox"
                data-testid="locationId-input"
              />
              <p className="text-center italic text-sm text-default mt-3">
                <FormattedMessage
                  id="login.locationIdHint"
                  defaultMessage="Location ID is found on the sticker on the kiosk, 3 or 4 digits."
                />
              </p>
            </div>
          )}
          {displayTerms && <LoginTerms confidentialityPolicyLink={confidentialityPolicyLink} legalNotice={legalNotice} />}
          {isBreakdown && (
            <BreakDownInformation onChange={handleChangeLoginData} dealerId={dealerId} brandId={brandId} />
          )}
        </main>

        <Footer
          disabled={isNextDisabled}
          onPrev={handleOnPrev}
          shouldDisplayBackButton
          onNext={handleNextClick}
          loading={isLoading || isSelfServiceLoading}
        />
      </div>
      {hasRestrictedAccess && isAtDealership && (
        <RestrictedAccessModal open={isRestrictedModalOpen} onClose={handleHideRestrictedAccessModal} />
      )}
      {isFlowTypeModalOpen && <FlowTypeModal />}
      {showRedirectionModal && (
        <BreakdownRedirectionModal plateNumber={registration} onCancel={handleHideRedirectionModal} />
      )}
    </>
  );
};

export default Login;
